import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter, Time } from 'lightweight-charts'
import { FillData } from './data'
import { FillSeriesOptions } from './options'
import { getOpacityFromHexCode } from '../../../../util/general'

interface FillBarItem {
    x: number;
    high: number;
    low: number;
}

export class FillSeriesRenderer<TData extends FillData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: FillSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: FillSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }
        const {color, fillGaps} = this._options;
        const {from, to} = this._data.visibleRange
        const renderLength = to - from;
        const data: FillBarItem[] = new Array(renderLength);

        const ctx = renderingScope.context
        ctx.beginPath()
        let outline = new Path2D()
        let currentStart = 0, previousBar: FillBarItem | null = null;

        for (let c=currentStart; c <= renderLength - 1 ; c++) {
            const bar = this._data.bars[c + from];
            const originalHigh = bar.originalData.high;
            const originalLow = bar.originalData.low;
            let high: number | null = null, low: number|null = null;
            if (Number.isFinite(originalHigh)) {
                high = (priceToCoordinate(originalHigh) as number)*window.devicePixelRatio;
            } else if (previousBar && fillGaps) {
                high = previousBar.high
            }
            
            if (Number.isFinite(originalLow)) {
                low = (priceToCoordinate(originalLow) as number)*window.devicePixelRatio;
            } else if (previousBar && fillGaps) {
                low = previousBar.low
            }
            const x = bar.x * renderingScope.horizontalPixelRatio
            
            if (high && low) {
                previousBar = {
                    x,
                    high,
                    low
                }
                data[c] = previousBar;
                if (c === currentStart) {
                    outline.moveTo(x, high)
                } else {
                    outline.lineTo(x, high)
                }

                if (c === renderLength - 1) {
                    this.fill(ctx, outline, data, currentStart, c)
                }
            } else {
                if (!previousBar) {
                    currentStart++;
                } else {
                    // console.log("chart: found a gap", c)
                    this.fill(ctx, outline, data, currentStart, c - 1)
                    ctx.beginPath();
                    previousBar = null;
                    outline = new Path2D();
                    currentStart = c + 1;
                }
            }

        }
    }

    fill(ctx: CanvasRenderingContext2D, path: Path2D, data: FillBarItem[], startIndex: number, lastIndex: number) {
        if (!this._options) return;
        const { color } = this._options;
        for (let i = lastIndex; i >= startIndex; i--) {
            const bar = data[i];
            if (!bar) {
                continue;
            }
            if (i === lastIndex) {
                path.lineTo(bar.x, bar.low);
            } else {
                path.lineTo(bar.x, bar.low)
            }
            if (i === startIndex) {
                path.lineTo(bar.x, bar.high)
            }
        }
        path.closePath();

        ctx.fillStyle = color;
        const opacity = getOpacityFromHexCode(color)
        ctx.globalAlpha = Number.isNaN(opacity) ? 0.3 : opacity;
        ctx.fill(path)

    }
}
